import { hasAsciiControlOrSpace } from '@/lib/validation';
import { isApplicationFilterValue, MAX_PROPERTY_SEARCH_LENGTH } from './native-property-list-query';

const RETURN_PARAMS = ['cursor', 'sort_direction', 'q', 'application'] as const;
const MAX_CURSOR_LENGTH = 2048;

/** Accept only an internal native property list address and its opaque list state. */
export function nativePropertyListReturnTo(value: string | null): string | null {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    value.includes('\u007f') ||
    hasAsciiControlOrSpace(value)
  )
    return null;
  try {
    const url = new URL(value, 'https://internal.invalid');
    const cursors = url.searchParams.getAll('cursor');
    const directions = url.searchParams.getAll('sort_direction');
    const searches = url.searchParams.getAll('q');
    const applications = url.searchParams.getAll('application');
    if (
      url.origin !== 'https://internal.invalid' ||
      url.pathname !== '/properties' ||
      url.hash ||
      [...url.searchParams.keys()].some(
        (key) => !(RETURN_PARAMS as readonly string[]).includes(key),
      ) ||
      cursors.length > 1 ||
      (cursors.length === 1 && (!cursors[0] || cursors[0].length > MAX_CURSOR_LENGTH)) ||
      directions.length > 1 ||
      (directions.length === 1 && !['asc', 'desc'].includes(directions[0] ?? '')) ||
      searches.length > 1 ||
      (searches.length === 1 &&
        (!searches[0] || searches[0].length > MAX_PROPERTY_SEARCH_LENGTH)) ||
      applications.length > 1 ||
      (applications.length === 1 && !isApplicationFilterValue(applications[0]))
    )
      return null;
    return url.pathname + url.search;
  } catch {
    return null;
  }
}

const MAX_NAME_HINT_LENGTH = 200;

/**
 * 목록에서 상세로 들어갈 때 라우터 state에 싣는 지점 이름 힌트. 상세가 지점 정보를 읽는 동안 경로(브레드크럼)에
 * "지점 상세" 같은 자리표시 대신 이름을 먼저 보여 주는 데만 쓴다 — 읽고 나면 응답의 이름이 그 자리를 대신한다.
 */
export interface NativePropertyNameHint {
  propertyId: string;
  propertyName: string;
}

export function nativePropertyNameHint(row: { id: string; name: string }): NativePropertyNameHint {
  return { propertyId: row.id, propertyName: row.name };
}

/** 라우터 state에서 이 주소의 지점에 대한 이름 힌트를 꺼낸다. 다른 지점의 힌트이거나 모양이 다르면 null이다. */
export function readNativePropertyNameHint(
  state: unknown,
  propertyId: string | undefined,
): string | null {
  if (!propertyId || typeof state !== 'object' || state === null) return null;
  const { propertyId: hintId, propertyName } = state as Partial<
    Record<keyof NativePropertyNameHint, unknown>
  >;
  if (hintId !== propertyId || typeof propertyName !== 'string') return null;
  return propertyName.trim() !== '' && propertyName.length <= MAX_NAME_HINT_LENGTH
    ? propertyName
    : null;
}
